import type { SearchOptions } from 'instantsearch.js';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import type { IntlShape } from 'react-intl/src/types';
import type { DeepNonNullable } from 'ts-essentials';

import type { Country, Locale } from '@/types/regionTypes';

import type { AddressSchema } from '@/components/AddressForm';
import type {
  OrderAppDeliveryMode,
  OrderAppError,
} from '@/graphql/orderApp/api';
import type {
  Address,
  AddressFragment,
  AddressInput,
  CheckoutFragment,
  CountryCode as SaleorCountryCode,
  MetadataInput,
  UserFragment,
} from '@/graphql/saleor/api';
import type { UseCheckout } from '@/hooks/useCheckout';
import { getIndexName } from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { StoreHit } from '@/integrations/algolia/types';
import {
  addressToInput,
  addressToSchema,
  isSameAddress,
  schemaToInput,
} from '@/lib/address';
import { MOCK_DEFAULT_EMAIL, MOCK_DEFAULT_VALUE } from '@/lib/const';
import { PREFER_NOT_TO_SAY } from '@/lib/forms/const';
import { type GenericErrorHandler } from '@/lib/forms/helpers';
import {
  ACCOUNT_METADATA,
  ACCOUNT_METADATA_VALUE,
  ONLINE_ACCOUNT_SOURCE,
  TIME_SLOT,
} from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { getPhoneCodeAndNumber, parsePhoneNumber } from '@/lib/phone';
import {
  getPhonePrefixSelectOptByCode,
  handlePhonePrefix,
} from '@/lib/phonePrefixes/helpers';
import { getRegionFromLocale } from '@/lib/regions';
import type { ApiError, Maybe } from '@/lib/types';
import type { ValidationRules } from '@/providers/ConfigProvider';
import { Modules } from '@/providers/ConfigProvider';
import type { CheckoutState } from '@/states/checkout';
import { checkoutState } from '@/states/checkout';
import type { UserState } from '@/states/user';
import * as M from '@/views/DeliveryView/messages';

import type { AddressFormInstance } from './components/types';
import type { DeliveryOption, DeliverySchema } from './schema';

export type LocaleSlug = 'en' | 'fr' | 'es' | 'it' | 'de' | 'zh';
export const shippingPoBoxErrorsTranslations: Record<LocaleSlug, string> = {
  en: 'We are currently unable to deliver to P.O. Box addresses. Please choose another delivery destination or contact our customer relations squad.',
  fr: 'Nous sommes actuellement pas en mesure de livrer aux adresses de boîtes postales. Veuillez choisir une autre destination de livraison ou contacter notre Squad des relations clients.',
  es: 'Actualmente no podemos realizar entregas a direcciones de apartados postales. Elija otro destino de entrega o póngase en contacto con nuestro Squad de atención al cliente.',
  it: "Al momento non siamo in grado di effettuare consegne a indirizzi di caselle postali. Scegli un'altra destinazione di consegna o contatta la nostra Customer Relations Squad.",
  de: 'Wir können derzeit nicht in an Postfachadressen liefern. Bitte wählen Sie einen anderen Lieferort oder kontaktieren Sie unsere Kundendienst-Squad.',
  zh: '我們目前無法配送到郵政信箱。請選擇其他的配送地點或聯繫我們的客戶關係團隊。',
};

export const shippingTerritoriesTranslations: Record<LocaleSlug, string> = {
  en: 'Shipping region is not supported, please choose a different zip code or country area',
  fr: "La région d'expédition n'est pas prise en charge, veuillez choisir un code postal ou une zone géographique différente",
  es: 'La región de envío no es compatible; elija un código postal o área de país diferente',
  it: "La regione di spedizione non è supportata, scegli un codice postale o un'area nazionale diversa",
  de: 'Die Versandregion wird nicht unterstützt. Bitte wählen Sie eine andere Postleitzahl oder ein anderes Ländergebiet',
  zh: '不支持运送区域，请选择不同的邮政编码或国家/地区',
};

export enum DELIVERY_MODES {
  BOUTIQUE = 'boutiquePickUp',
  HOME = 'homeDelivery',
}
export const translateShippingErrors = (
  errors: OrderAppError[],
  locale: LocaleSlug | undefined,
) => {
  return errors.forEach((error) => {
    if (locale && error.code === 'shipping_address.po_box_not_supported') {
      error.message = shippingPoBoxErrorsTranslations[locale] || error.message;
    }
    if (locale && error.code === 'shipping_address.blacklisted') {
      error.message = shippingTerritoriesTranslations[locale] || error.message;
    }
  });
};
export const API_FIELDS_MAPPING = {
  streetAddress1: ['streetName', 'deliveryId'],
};

export const getCommonUserInfo = (
  routerLocale: string,
  billingAddress?: AddressFragment,
  joinClub?: boolean,
  userAffiliated?: boolean,
) => {
  if (isEmpty(billingAddress))
    return { firstName: '', lastName: '', metadata: [] };
  const firstName = billingAddress?.firstName || '';
  const lastName = billingAddress?.lastName || '';
  const { country, locale } = getRegionFromLocale(routerLocale);

  const metadata = [
    {
      key: ACCOUNT_METADATA.accountSource,
      value: ONLINE_ACCOUNT_SOURCE.STOREFRONT_CHECKOUT, // Auto create the web account during guest checkout flow
    },
    {
      key: ACCOUNT_METADATA.country,
      value: country.code,
    },
    {
      key: ACCOUNT_METADATA.language,
      value: locale.sfCode,
    },
    {
      key: ACCOUNT_METADATA.newsletterConsent,
      value: 'false',
    },
  ];

  if (country.code === 'JP') {
    metadata.push({
      key: ACCOUNT_METADATA.first_name_kanji,
      value:
        getMetaValue(
          billingAddress?.metadata,
          ACCOUNT_METADATA.first_name_kanji,
        ) ?? firstName,
    });
    metadata.push({
      key: ACCOUNT_METADATA.last_name_kanji,
      value:
        getMetaValue(
          billingAddress?.metadata,
          ACCOUNT_METADATA.last_name_kanji,
        ) ?? lastName,
    });

    if (joinClub) {
      metadata.push({
        key: ACCOUNT_METADATA.isClubMember,
        value: 'true',
      });
    }
  }

  if (userAffiliated) {
    metadata.push({
      key: ACCOUNT_METADATA.isAffiliated,
      value: 'true',
    });
  }

  if (billingAddress.phone) {
    const { phoneCode, phoneNumber } = getPhoneCodeAndNumber(
      billingAddress.phone,
    );

    if (phoneCode && phoneNumber) {
      metadata.push({
        key: ACCOUNT_METADATA.phonePrefix,
        value: phoneCode,
      });
      metadata.push({
        key: ACCOUNT_METADATA.phone,
        value: phoneNumber,
      });
    }
  }

  return { firstName, lastName, metadata };
};

export const StoresSearch = async <TObj = StoreHit>(
  query: string,
  locale: string,
  params?: SearchOptions,
  draftMode?: boolean,
) => {
  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
  });
  return algoliaSearchClient
    .initIndex(getIndexName('STORE', locale))
    .search<TObj>(query, params);
};

const isAddressPopulated = (address?: AddressFragment | null): boolean => {
  if (!address) return false;
  return Boolean(
    address.firstName &&
    address.lastName &&
    address.streetAddress1 &&
    address.city &&
    address.postalCode,
  );
};

const getCleanDefaultShippingAddress = (
  checkout: DeepNonNullable<CheckoutFragment>,
  user: Partial<UserFragment> | null,
  country: Country,
): AddressFragment => {
  const defaultAddress = getDefaultAddress(user, country);
  const shouldIgnoreCheckoutShipping =
    checkout?.shippingAddress?.firstName === MOCK_DEFAULT_VALUE ||
    checkout?.shippingAddress?.lastName === MOCK_DEFAULT_VALUE ||
    checkout?.shippingAddress?.streetAddress1 === MOCK_DEFAULT_VALUE;

  if (
    shouldIgnoreCheckoutShipping ||
    checkout.deliveryMode === Modules.BOUTIQUE_PICKUP // Edge case when we switch from a boutique pickup to a home delivery
  ) {
    return {
      id: '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      cityArea: '',
      countryArea: '',
      country: { code: country.code, country: country.name },
      metadata: [],
      postalCode: '',
      companyName: '',
    } as AddressFragment;
  }

  if (isAddressPopulated(checkout.shippingAddress)) {
    return {
      ...checkout.shippingAddress,
      id: checkout.shippingAddress?.id || '',
      firstName: checkout?.shippingAddress?.firstName || user?.firstName || '',
      lastName: checkout?.shippingAddress?.lastName || user?.lastName || '',
      streetAddress1: checkout?.shippingAddress?.streetAddress1 || '',
      streetAddress2: checkout?.shippingAddress?.streetAddress2 || '',
      city: checkout?.shippingAddress?.city || '',
      cityArea: checkout?.shippingAddress?.cityArea || '',
      countryArea: checkout?.shippingAddress?.countryArea || '',
      country: { code: country.code, country: country.name },
      metadata: checkout.shippingAddress?.metadata || [],
      postalCode: checkout?.shippingAddress?.postalCode || '',
      companyName: checkout?.shippingAddress?.companyName || '',
    } as AddressFragment;
  }

  if (defaultAddress) {
    return {
      ...checkout.shippingAddress,
      id: defaultAddress.id || checkout.shippingAddress?.id || '',
      firstName: defaultAddress.firstName || user?.firstName || '',
      lastName: defaultAddress.lastName || user?.lastName || '',
      streetAddress1: defaultAddress.streetAddress1 || '',
      streetAddress2: defaultAddress.streetAddress2 || '',
      city: defaultAddress.city || '',
      postalCode: defaultAddress.postalCode || '',
      companyName: defaultAddress.companyName || '',
      cityArea:
        defaultAddress.cityArea || checkout.shippingAddress?.cityArea || '',
      countryArea:
        defaultAddress.countryArea ||
        checkout.shippingAddress?.countryArea ||
        '',
      country: checkout.shippingAddress?.country ||
        defaultAddress.country || {
          code: country.code,
          country: country.name,
        },
      metadata:
        defaultAddress.metadata || checkout.shippingAddress?.metadata || [],
    } as AddressFragment;
  }

  return {
    id: '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    cityArea: '',
    countryArea: '',
    country: { code: country.code, country: country.name },
    metadata: [],
    postalCode: '',
    companyName: '',
  } as AddressFragment;
};

const getCleanDefaultBillingAddress = (
  checkout: DeepNonNullable<CheckoutFragment>,
  user: Partial<UserFragment> | null,
  country: Country,
): AddressFragment => {
  const shouldIgnoreCheckoutBilling =
    checkout?.billingAddress?.firstName === MOCK_DEFAULT_VALUE ||
    checkout?.billingAddress?.lastName === MOCK_DEFAULT_VALUE ||
    checkout?.billingAddress?.streetAddress1 === MOCK_DEFAULT_VALUE;

  if (shouldIgnoreCheckoutBilling) {
    return {
      id: '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      cityArea: '',
      countryArea: '',
      country: { code: country.code, country: country.name },
      metadata: [],
      postalCode: '',
      companyName: '',
    } as AddressFragment;
  }

  if (isAddressPopulated(checkout.billingAddress)) {
    const addr = checkout.billingAddress as AddressFragment;
    return { ...addr, id: addr.id || '' } as AddressFragment;
  }

  if (user?.defaultBillingAddress) {
    const addr = user.defaultBillingAddress as AddressFragment;
    return { ...addr, id: addr.id || '' } as AddressFragment;
  }

  return {
    ...checkout.billingAddress,
    id: checkout.billingAddress?.id || '',
    firstName: checkout?.billingAddress?.firstName || user?.firstName || '',
    lastName: checkout?.billingAddress?.lastName || user?.lastName || '',
    streetAddress1: checkout?.billingAddress?.streetAddress1 || '',
    streetAddress2: checkout?.billingAddress?.streetAddress2 || '',
    city: checkout?.billingAddress?.city || '',
    cityArea: checkout?.billingAddress?.cityArea || '',
    countryArea: checkout?.billingAddress?.countryArea || '',
    country: checkout.billingAddress?.country || {
      code: country.code,
      country: country.name,
    },
    metadata: checkout.billingAddress?.metadata || [],
    postalCode: checkout?.billingAddress?.postalCode || '',
    companyName: checkout?.billingAddress?.companyName || '',
  } as AddressFragment;
};

export const getDefaultAddresses = (
  checkout: DeepNonNullable<CheckoutFragment>,
  user: Partial<UserFragment> | null,
  country: Country,
): {
  cleanDefaultBillingAddress: AddressFragment;
  cleanDefaultShippingAddress: AddressFragment;
} => {
  const cleanDefaultShippingAddress = getCleanDefaultShippingAddress(
    checkout,
    user,
    country,
  );
  const cleanDefaultBillingAddress = getCleanDefaultBillingAddress(
    checkout,
    user,
    country,
  );

  return {
    cleanDefaultShippingAddress,
    cleanDefaultBillingAddress,
  };
};

export const getSchemaDefaults = (
  country: Country,
  checkout: DeepNonNullable<CheckoutFragment>,
  phonePrefixes: string[],
  shippingRules: Maybe<ValidationRules>,
  billingRules: Maybe<ValidationRules>,
  deliveryMethods: Maybe<OrderAppDeliveryMode[]>,
  user: Partial<UserFragment> | null,
  formatMessage: IntlShape['formatMessage'],
) => {
  const getDeliveryDefaultOption = (): DeliveryOption => {
    if (!deliveryMethods?.length) {
      return '';
    }
    if (
      !checkout.isShippingRequired ||
      (deliveryMethods?.includes('BOUTIQUE_PICKUP') &&
        checkout?.deliveryMode === 'BOUTIQUE_PICKUP') ||
      (!deliveryMethods?.includes('HOME_DELIVERY') &&
        checkout?.deliveryMode === 'HOME_DELIVERY')
    ) {
      return 'boutiquePickUp';
    }
    return 'homeDelivery';
  };
  const userPhone =
    getMetaValue(user?.metadata || [], ACCOUNT_METADATA.phone) ?? '';
  const userPhonePrefix = handlePhonePrefix(
    getMetaValue(user?.metadata || [], ACCOUNT_METADATA.phonePrefix) ?? '',
  );
  const billingPhone = checkout.billingAddress?.phone;
  const phone = billingPhone
    ? (getPhoneCodeAndNumber(billingPhone).phoneNumber ?? userPhone)
    : userPhone;
  const countryCode: SaleorCountryCode = ((billingPhone &&
    parsePhoneNumber(billingPhone)?.country) ||
    (userPhone &&
      userPhonePrefix &&
      parsePhoneNumber(userPhonePrefix + userPhone)?.country) ||
    country.code) as SaleorCountryCode;
  let shippingAddressFirstName: Maybe<string> =
    checkout.shippingAddress?.firstName;
  let shippingAddressLastName: Maybe<string> =
    checkout.shippingAddress?.lastName;
  let billingAddressFirstName: Maybe<string> =
    checkout.billingAddress?.firstName;
  let billingAddressLastName: Maybe<string> = checkout.billingAddress?.lastName;
  let shippingAddressTitle: Maybe<string> = checkout.shippingTitle;
  let billingAddressTitle: Maybe<string> = checkout.billingTitle;

  if (!isEmpty(user)) {
    let userFirstName;
    let userLastName;
    if (countryCode === 'JP') {
      userFirstName = user?.metadata
        ? getMetaValue(user?.metadata, ACCOUNT_METADATA.first_name_kanji)
        : user?.firstName;
      userLastName = user?.metadata
        ? getMetaValue(user?.metadata, ACCOUNT_METADATA.last_name_kanji)
        : user?.lastName;
    } else {
      userFirstName = user?.firstName;
      userLastName = user?.lastName;
    }
    shippingAddressFirstName = shippingAddressFirstName ?? userFirstName;
    shippingAddressLastName = shippingAddressLastName ?? userLastName;
    billingAddressFirstName = billingAddressFirstName ?? userFirstName;
    billingAddressLastName = billingAddressLastName ?? userLastName;
    shippingAddressTitle =
      shippingAddressTitle ||
      getMetaValue(
        user.defaultShippingAddress?.metadata ?? [],
        ACCOUNT_METADATA.personalTitle,
      ) ||
      getMetaValue(user.metadata ?? [], ACCOUNT_METADATA.personalTitle);
    billingAddressTitle =
      billingAddressTitle ||
      getMetaValue(
        user.defaultBillingAddress?.metadata ?? [],
        ACCOUNT_METADATA.personalTitle,
      ) ||
      getMetaValue(user.metadata ?? [], ACCOUNT_METADATA.personalTitle);
  }

  const { cleanDefaultBillingAddress, cleanDefaultShippingAddress } =
    getDefaultAddresses(checkout, user, country);

  const defaultValues: DeliverySchema = {
    shippingAddress: {
      ...addressToSchema(
        shippingAddressTitle,
        cleanDefaultShippingAddress,
        shippingRules?.countryAreaChoices ?? [],
        shippingRules?.cityChoices ?? [],
        formatMessage,
        shippingAddressFirstName,
        shippingAddressLastName,
      ),
      country: {
        label: checkout.shippingAddress?.country?.country ?? country.name,
        value:
          (checkout?.shippingAddress?.country?.code as SaleorCountryCode) ??
          country.code,
      },
    },
    billingAddress: {
      ...addressToSchema(
        billingAddressTitle,
        cleanDefaultBillingAddress,
        billingRules?.countryAreaChoices ?? [],
        billingRules?.cityChoices ?? [],
        formatMessage,
        billingAddressFirstName,
        billingAddressLastName,
      ),
      country: {
        label: checkout.billingAddress?.country?.country ?? country.name,
        value:
          (checkout?.billingAddress?.country?.code as SaleorCountryCode) ??
          country.code,
      },
    },
    email:
      checkout?.user?.email ||
      (checkout?.email && checkout?.email !== MOCK_DEFAULT_EMAIL
        ? checkout.email
        : user?.email || ''),
    differentBillingAddress: !!(
      checkout.billingAddress &&
      !isSameAddress(checkout.shippingAddress, checkout.billingAddress, [
        'phone',
      ])
    ),
    deliveryOption: getDeliveryDefaultOption(),
    deliveryId: checkout.boutiqueExtRef || '',
    isGift: checkout.isGift === 'true',
    giftForm: {
      from: checkout.giftFrom || '',
      message: checkout.giftMessage || '',
    },
    socialClubTermsAccepted: checkout.orderFlowScTermsAccepted === 'true',
    phone: phone,
    phonePrefix: getPhonePrefixSelectOptByCode(countryCode),
    timeSlot: checkout.isShippingRequired
      ? {
          value: checkout.timeSlot || TIME_SLOT.BEFORE_9,
          label:
            checkout.timeSlot || formatMessage(M.TimeSlotLabelMessages.before9),
        }
      : null,
    shippingAddressSelectedAddressId: cleanDefaultShippingAddress.id || '',
    billingAddressSelectedAddressId: cleanDefaultBillingAddress.id || '',
  };

  // Dunno why types are incompatible
  return defaultValues as any; //NOSONAR
};

export const shippingAddressPromise = async (
  selectedAddress: Maybe<AddressFragment>,
  address: AddressSchema,
  isHomeDelivery: boolean,
  mutation: UseCheckout['addressUpdate'],
  errorHandler: GenericErrorHandler,
  phonePrefix: string,
  phone: string,
  locale?: Locale,
) =>
  new Promise<boolean>((resolve) => {
    /**
     * Shipping address update.
     */
    (async () => {
      const phoneWithPrefix = `${phonePrefix}${phone}`;
      const addressInput = selectedAddress
        ? // Take the `fresh` phone (in case updated) from the contact form.
          addressToInput({ ...selectedAddress, phone: phoneWithPrefix })
        : { ...schemaToInput(address), phone: phoneWithPrefix };

      const shouldUpdate =
        isHomeDelivery &&
        (checkoutState.checkout?.shippingAddress
          ? !isSameAddress(
              addressInput,
              addressToInput(checkoutState.checkout?.shippingAddress),
            )
          : true);
      const metadata = [...(addressInput.metadata ?? [])];

      if (!selectedAddress)
        metadata.push({
          key: ACCOUNT_METADATA.addressSaved,
          value: ACCOUNT_METADATA_VALUE.addressSaved,
        });

      addressInput.metadata = metadata as MetadataInput[];
      if (!shouldUpdate) {
        return resolve(true);
      }

      const errors = await mutation('shipping', addressInput);
      if (errors?.length) {
        translateShippingErrors(
          errors as OrderAppError[],
          (locale?.slug as LocaleSlug) || undefined,
        );
        errorHandler(errors, {
          schemaPrefix: 'shippingAddress',
          fieldsMapping: API_FIELDS_MAPPING,
        });
        resolve(false);
      } else {
        resolve(true);
      }
    })();
  });

export const billingAddressPromise = async (
  selectedAddress: Maybe<AddressFragment>,
  billingAddress: AddressSchema,
  mutation: UseCheckout['addressUpdate'],
  errorHandler: GenericErrorHandler,
  phonePrefix?: string,
  phone?: string,
  isDifferentBillingAddress?: boolean,
) =>
  new Promise<boolean>((resolve) => {
    /**
     * Billing address update.
     */
    (async () => {
      const phoneWithPrefix = `${phonePrefix}${phone}`;
      const billingAddressInput = schemaToInput(billingAddress);
      const address = selectedAddress
        ? addressToInput({
            ...selectedAddress,
            phone: phoneWithPrefix,
          })
        : { ...billingAddressInput, phone: phoneWithPrefix };

      if (
        !checkoutState.checkout?.billingAddress ||
        !isSameAddress(
          address,
          addressToInput(checkoutState.checkout?.billingAddress),
          // ['phone'],
        )
      ) {
        address.metadata = (address.metadata ?? []) as MetadataInput[];
        if (isDifferentBillingAddress && !selectedAddress) {
          address.metadata.push({
            key: ACCOUNT_METADATA.addressSaved,
            value: ACCOUNT_METADATA_VALUE.addressSaved,
          });
        }

        const errors = await mutation('billing', address);
        if (errors) {
          errorHandler(errors, {
            schemaPrefix: 'billingAddress',
            fieldsMapping: API_FIELDS_MAPPING,
          });

          return resolve(false);
        }
      }

      resolve(true);
    })();
  });

export const emailUpdatePromise = async (
  email: Maybe<string>,
  isEmailDirty: boolean,
  mutation: UseCheckout['emailUpdate'],
  errorHandler: GenericErrorHandler,
) =>
  new Promise<boolean>((resolve) => {
    /**
     * Email update for guest checkout.
     */
    (async () => {
      if (email && checkoutState.isGuestCheckout && isEmailDirty) {
        const errors = await mutation(email);
        if (errors) {
          errorHandler(errors);
          return resolve(false);
        }
      }
      resolve(true);
    })();
  });

export const deliveryUpdatePromise = async (
  selectedDeliveryId: Maybe<string>,
  isBoutiquePickUp: boolean,
  selectedShippingAddress: Maybe<AddressFragment>,
  shippingAddress: AddressSchema,
  selectedBillingAddress: Maybe<AddressFragment>,
  billingAddress: AddressSchema,
  mutation: UseCheckout['deliveryUpdate'],
  errorHandler: GenericErrorHandler,
  phonePrefix: string,
  phone: string,
) =>
  new Promise<boolean>((resolve) => {
    /**
     * Delivery method set.
     */
    (async () => {
      const phoneWithPrefix = `${phonePrefix}${phone}`;
      const shippingAddressInput = selectedShippingAddress
        ? // Take the `fresh` phone (in case updated) from the contact form.
          addressToInput({
            ...selectedShippingAddress,
            phone: phoneWithPrefix,
          })
        : { ...schemaToInput(shippingAddress), phone: phoneWithPrefix };
      const billingAddressInput = selectedBillingAddress
        ? // Take the `fresh` phone (in case updated) from the contact form.
          addressToInput({
            ...selectedBillingAddress,
            phone: phoneWithPrefix,
          })
        : { ...schemaToInput(billingAddress), phone: phoneWithPrefix };
      const deliveryMode: OrderAppDeliveryMode = isBoutiquePickUp
        ? 'BOUTIQUE_PICKUP'
        : 'HOME_DELIVERY';
      const boutiqueExtReference = isBoutiquePickUp ? selectedDeliveryId : '';
      const address = isBoutiquePickUp
        ? pick(billingAddressInput, ['firstName', 'lastName', 'phone'])
        : shippingAddressInput;

      const addressWithUpdatedMetadata: AddressInput = !isBoutiquePickUp
        ? address
        : {
            ...address,
            metadata: [
              ...(checkoutState.checkout?.shippingAddress?.metadata ?? []).map(
                ({ key, value }) => ({
                  key,
                  value:
                    key === ACCOUNT_METADATA.vatNumber ||
                    key === ACCOUNT_METADATA.abn
                      ? ''
                      : (value ?? ''),
                }),
              ),
            ],
          };

      const errors = await mutation(
        deliveryMode,
        boutiqueExtReference,
        addressWithUpdatedMetadata,
      );

      if (errors) {
        errorHandler(errors);
        return resolve(false);
      }
      resolve(true);
    })();
  });

export const addressTitleUpdatePromise = async (
  mutation: UseCheckout['addressTitleUpdate'],
  shippingAddress: AddressSchema,
  billingAddress: AddressSchema,
  differentBillingAddress: boolean,
  errorHandler: GenericErrorHandler,
) =>
  /**
   * Set shipping/billing title (Mr. etc).
   */
  new Promise<boolean>((resolve) => {
    (async () => {
      const shippingTitle = shippingAddress.title;
      const billingTitle = differentBillingAddress
        ? billingAddress.title
        : shippingAddress.title;

      if (
        shippingTitle === checkoutState.checkout?.shippingTitle &&
        billingTitle === checkoutState.checkout?.billingTitle
      ) {
        return resolve(true);
      }

      const errors = await mutation(
        shippingTitle?.value ?? '',
        billingTitle?.value ?? '',
      );

      if (errors.length) {
        errorHandler(errors, { errorType: 'metadata' });
        return resolve(false);
      }

      resolve(true);
    })();
  });

export const giftUpdatePromise = async (
  isGift: boolean,
  from: string,
  fromMessage: string,
  mutationUpdate: UseCheckout['giftUpdate'],
  mutationDelete: UseCheckout['giftDelete'],
  errorHandler: GenericErrorHandler,
) =>
  /**
   * Set/unset order as a gift.
   */
  new Promise<boolean>((resolve) => {
    (async () => {
      let metadataErrors: ApiError[] = [];

      if (isGift) {
        if (
          checkoutState.checkout?.giftFrom !== from ||
          checkoutState.checkout?.giftMessage !== fromMessage
        ) {
          metadataErrors = await mutationUpdate(from, fromMessage);
        }
      } else {
        if (
          checkoutState.checkout?.giftFrom &&
          checkoutState.checkout?.giftMessage
        ) {
          metadataErrors = await mutationDelete();
        }
      }

      if (metadataErrors.length) {
        errorHandler(metadataErrors, { errorType: 'metadata' });
        return resolve(false);
      }

      resolve(true);
    })();
  });

export const deliveryScheduleUpdatePromise = async (
  timeSlot: DeliverySchema['timeSlot'],
  mutationUpdate: UseCheckout['deliveryScheduleUpdate'],
  mutationDelete: UseCheckout['deliveryScheduleDelete'],
  errorHandler: GenericErrorHandler,
  shouldResolve: boolean,
) =>
  /**
   * Set/unset delivery schedule.
   */
  new Promise<boolean>((resolve) => {
    (async () => {
      if (!shouldResolve) {
        return resolve(true);
      }

      let metadataErrors: ApiError[] = [];

      const boutiquePickUpUnchanged =
        !timeSlot && checkoutState.checkout?.timeSlot === timeSlot;

      const homeDeliveryUnChanged = !!(
        timeSlot && checkoutState.checkout?.timeSlot === timeSlot.value
      );

      if (boutiquePickUpUnchanged || homeDeliveryUnChanged) {
        return resolve(true);
      }

      if (timeSlot) {
        metadataErrors = await mutationUpdate(timeSlot.value);
      } else {
        metadataErrors = await mutationDelete();
      }

      if (metadataErrors.length) {
        errorHandler(metadataErrors, { errorType: 'metadata' });
        return resolve(false);
      }

      resolve(true);
    })();
  });

export type PhonePrefix = {
  label: string;
  value: string;
};

export const validateAddress = async (
  refElement: AddressFormInstance | null,
): Promise<boolean> => {
  return refElement?.validateAddressUsingMelissa() ?? false;
};

export const validateAddresses = async (
  formData: DeliverySchema,
  isShippingRequired: boolean,
  shippingAddressForm: AddressFormInstance | null,
  billingAddressForm: AddressFormInstance | null,
): Promise<boolean> => {
  const isShippingAddressValid =
    isShippingRequired && formData.deliveryOption !== 'boutiquePickUp'
      ? await validateAddress(shippingAddressForm)
      : true;

  const isBillingAddressValid = !formData.differentBillingAddress
    ? true
    : await validateAddress(billingAddressForm);

  return isShippingAddressValid && isBillingAddressValid;
};

// Define types for the parameters
export interface AddressParams {
  addressTitleUpdate: UseCheckout['addressTitleUpdate'];
  addressUpdate: UseCheckout['addressUpdate'];
  selectedBillingAddress?: Address;
  selectedShippingAddress?: Address;
}

export interface DeliveryParams {
  deliveryScheduleDelete: UseCheckout['deliveryScheduleDelete'];
  deliveryScheduleUpdate: UseCheckout['deliveryScheduleUpdate'];
  formData: DeliverySchema;
  hasDeliverySchedule: boolean;
}

export interface EmailParams {
  emailUpdate: UseCheckout['emailUpdate'];
  isEmailChanged: boolean;
}

export interface GiftParams {
  formData: DeliverySchema;
  giftDelete: UseCheckout['giftDelete'];
  giftUpdate: UseCheckout['giftUpdate'];
}

export interface ErrorHandlerParams {
  errorHandler: GenericErrorHandler;
}

export interface PreparePromisesParams {
  addressParams: AddressParams;
  checkout: CheckoutState['checkout'];
  deliveryParams: DeliveryParams;
  emailParams: EmailParams;
  errorHandlerParams: ErrorHandlerParams;
  formData: DeliverySchema;
  giftParams: GiftParams;
  locale?: Locale;
}

export const preparePromises = ({
  addressParams,
  checkout,
  deliveryParams,
  emailParams,
  errorHandlerParams,
  formData,
  giftParams,
  locale,
}: PreparePromisesParams) => {
  const promises = [];

  const {
    addressTitleUpdate,
    addressUpdate,
    selectedBillingAddress,
    selectedShippingAddress,
  } = addressParams;
  const {
    deliveryScheduleDelete,
    deliveryScheduleUpdate,
    hasDeliverySchedule,
  } = deliveryParams;
  const { emailUpdate, isEmailChanged } = emailParams;
  const { giftDelete, giftUpdate } = giftParams;
  const { errorHandler } = errorHandlerParams;

  const phonePrefix = formData.phonePrefix.value;
  const phone = getPhoneCodeAndNumber(
    `${formData.phonePrefix.value}${formData.phone}`,
  ).phoneNumber;

  if (checkout?.isShippingRequired) {
    promises.push(
      shippingAddressPromise(
        selectedShippingAddress,
        formData?.shippingAddress,
        formData.deliveryOption === 'homeDelivery',
        addressUpdate,
        errorHandler,
        phonePrefix,
        phone ?? '',
        locale || undefined,
      ),
      addressTitleUpdatePromise(
        addressTitleUpdate,
        formData?.shippingAddress,
        formData.billingAddress,
        formData.differentBillingAddress,
        errorHandler,
      ),
    );
  }

  promises.push(
    billingAddressPromise(
      formData.differentBillingAddress
        ? selectedBillingAddress
        : selectedShippingAddress,
      formData.differentBillingAddress ||
        formData.deliveryOption !== 'homeDelivery'
        ? formData.billingAddress
        : formData?.shippingAddress,
      addressUpdate,
      errorHandler,
      phonePrefix,
      phone,
      formData.differentBillingAddress,
    ),
    emailUpdatePromise(
      formData.email,
      isEmailChanged,
      emailUpdate,
      errorHandler,
    ),
    giftUpdatePromise(
      formData.isGift,
      formData.giftForm.from,
      formData.giftForm.message,
      giftUpdate,
      giftDelete,
      errorHandler,
    ),
    deliveryScheduleUpdatePromise(
      formData.timeSlot,
      deliveryScheduleUpdate,
      deliveryScheduleDelete,
      errorHandler,
      hasDeliverySchedule,
    ),
  );

  return promises;
};

export const getSelectedAddress = (
  addresses?: Address[],
  selectedAddressId?: string,
) => {
  const selectedAddress = addresses?.find(({ id }) => id === selectedAddressId);
  return (
    selectedAddress && {
      ...selectedAddress,
      metadata: selectedAddress.metadata
        .map((item) => pick(item, ['key', 'value']))
        .filter((item) => item.key !== ACCOUNT_METADATA.addressSaved),
    }
  );
};

export const getDefaultAddress = (
  user: UserState['user'],
  country: Country,
) => {
  const firstShippingAddress = user?.addresses?.find(
    (address) => address.country.code === country.code,
  );
  const shippingAddressToSet =
    user?.defaultShippingAddress?.country.code === country.code
      ? user?.defaultShippingAddress
      : firstShippingAddress;

  if (!shippingAddressToSet) {
    return null;
  }

  let metaData = shippingAddressToSet?.metadata;
  if (shippingAddressToSet?.metadata) {
    metaData = shippingAddressToSet.metadata.filter(
      (item) => item.key !== ACCOUNT_METADATA.addressSaved,
    );
  }

  return { ...shippingAddressToSet, metaData };
};

/**
 * Determines the title value to save to user metadata, if any.
 *
 * Rules:
 * - If the user already has a non-empty personalTitle → null (don't update)
 * - If the user has no personalTitle and address name matches user → address title
 * - If the user has no personalTitle and address name differs → "NOTTOSAY"
 */
export const getUserTitleToSave = ({
  addressFirstName,
  addressLastName,
  addressTitle,
  userFirstName,
  userLastName,
  userMetadata,
}: {
  addressFirstName?: string;
  addressLastName?: string;
  addressTitle?: string | null;
  userFirstName?: string | null;
  userLastName?: string | null;
  userMetadata?: readonly { key: string; value: string }[];
}): string | null => {
  const existingTitle = getMetaValue(
    userMetadata ?? [],
    ACCOUNT_METADATA.personalTitle,
  );
  if (existingTitle) return null;

  const isUserAddress =
    addressFirstName === userFirstName && addressLastName === userLastName;

  return isUserAddress ? addressTitle || null : PREFER_NOT_TO_SAY;
};

export type TitleResolution = {
  /**
   * True only for home delivery when the shipping title is missing.
   * The caller must show an error and stop progression.
   */
  shippingTitleError: boolean;
  /**
   * When non-null, the caller should auto-set the shipping title to this value
   * before submitting (boutique delivery only).
   */
  shippingTitleToSet: typeof PREFER_NOT_TO_SAY | null;
  /**
   * When non-null, the caller should auto-set the billing title to this value
   * before submitting.
   */
  billingTitleToSet: typeof PREFER_NOT_TO_SAY | null;
};

/**
 * Resolves what to do with missing address titles at form-submission time.
 *
 * Decision table:
 * - Home delivery + shipping title missing  → shippingTitleError (stop, show error)
 * - Home delivery + billing title missing   → auto-set billing to PREFER_NOT_TO_SAY
 * - Boutique delivery + any title missing   → auto-set that title to PREFER_NOT_TO_SAY
 *
 * The user must NEVER be blocked on the delivery step without a visible reason.
 */
export const resolveSubmitTitles = (
  deliveryOption: DeliveryOption | string,
  shippingTitleValue: string | null | undefined,
  billingTitleValue: string | null | undefined,
): TitleResolution => {
  const shippingMissing = !shippingTitleValue;
  const billingMissing = !billingTitleValue;
  const isBoutique = deliveryOption === DELIVERY_MODES.BOUTIQUE;

  if (isBoutique) {
    return {
      shippingTitleError: false,
      shippingTitleToSet: shippingMissing ? PREFER_NOT_TO_SAY : null,
      billingTitleToSet: billingMissing ? PREFER_NOT_TO_SAY : null,
    };
  }

  // Home delivery: shipping title is mandatory; billing can be auto-filled.
  // When shipping title is missing we stop immediately — skip billing resolution.
  if (shippingMissing) {
    return {
      shippingTitleError: true,
      shippingTitleToSet: null,
      billingTitleToSet: null,
    };
  }

  return {
    shippingTitleError: false,
    shippingTitleToSet: null,
    billingTitleToSet: billingMissing ? PREFER_NOT_TO_SAY : null,
  };
};

/**
 * Surfaces the most relevant validation error from an address field error map
 *
 * Returns true if an error was set, false if no action was taken
 */
export const surfaceCardError = (
  errs: Record<string, { message?: string }> | undefined,
  hasSelectedId: boolean,
  setError: (msg: string) => void,
  addressMissingTitleMsg: string,
  requiredFieldMsg: string,
): boolean => {
  if (!errs || !hasSelectedId) return false;
  const hasTitleErr = !!errs?.title?.message;
  const firstMsg = Object.values(errs).find((e) => e?.message)?.message;
  setError(
    hasTitleErr ? addressMissingTitleMsg : (firstMsg ?? requiredFieldMsg),
  );
  return true;
};
